import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, Trash2, Paperclip, X, ImageIcon } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getChatHistory, sendChat, clearChat, apiChatPhoto, formatApiError } from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";
import ChatPlanCard from "@/components/ChatPlanCard";

const OWL_AVATAR = "https://images.unsplash.com/photo-1725216548591-036c4b8633b9?w=100";

const SUGGESTIONS = [
    "What should I sow this week?",
    "Build me a 4×8 raised bed with tomatoes and basil.",
    "Add 6 carrots to my empty bed.",
    "Plan succession lettuce sowings for spring.",
];

export default function Chat() {
    const { activeGardenId, refresh: refreshGardens } = useGardens();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pendingPhoto, setPendingPhoto] = useState(null);  // {file, previewUrl}
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const scrollRef = useRef(null);

    const load = async () => {
        try {
            const r = await getChatHistory("default");
            setMessages(r.messages || []);
        } catch (e) {
            // After the API client's automatic retries (see lib/api.js)
            // a final failure usually means the network is genuinely
            // down. Fall back to an empty conversation rather than
            // toasting — the user can still type a new message.
            // eslint-disable-next-line no-console
            console.warn("chat history fetch failed; starting empty", e?.message);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending]);

    // Cleanup the local object URL preview when the file changes / unmounts.
    useEffect(() => {
        return () => { if (pendingPhoto?.previewUrl) URL.revokeObjectURL(pendingPhoto.previewUrl); };
    }, [pendingPhoto?.previewUrl]);

    const stagePhoto = (file) => {
        if (!file) return;
        const okTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
        if (!okTypes.includes((file.type || "").toLowerCase())) {
            toast.error("Please drop a JPG, PNG, WEBP or HEIC image.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Image too large (max 10 MB).");
            return;
        }
        if (pendingPhoto?.previewUrl) URL.revokeObjectURL(pendingPhoto.previewUrl);
        setPendingPhoto({ file, previewUrl: URL.createObjectURL(file) });
    };

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (f) stagePhoto(f);
        e.target.value = "";  // allow re-selecting same file
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) stagePhoto(f);
    };

    const onPaste = (e) => {
        const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith("image/"));
        if (item) {
            const f = item.getAsFile();
            if (f) stagePhoto(f);
        }
    };

    const sendPhotoMessage = async () => {
        if (!pendingPhoto?.file || sending) return;
        setSending(true);
        const optimistic = {
            id: `tmp-${Date.now()}`,
            role: "user",
            content: (input || "Take a look at this photo from my garden.").trim(),
            _previewUrl: pendingPhoto.previewUrl,
        };
        setMessages((m) => [...m, optimistic]);
        const filenameToSend = pendingPhoto.file;
        const promptToSend = input.trim();
        setInput("");
        try {
            const res = await apiChatPhoto(filenameToSend, {
                sessionId: "default",
                prompt: promptToSend || undefined,
            });
            setMessages((m) => [
                ...m.filter(x => x.id !== optimistic.id),
                res.user_message,
                res.assistant_message,
            ]);
            if (res.quota && !res.quota.unlimited && res.quota.remaining === 0) {
                toast("You've used your free photo diagnoses for the month.");
            }
        } catch (e) {
            toast.error(formatApiError(e));
            setMessages((m) => m.filter(x => x.id !== optimistic.id));
        } finally {
            // Drop the photo regardless of success.
            if (pendingPhoto.previewUrl) URL.revokeObjectURL(pendingPhoto.previewUrl);
            setPendingPhoto(null);
            setSending(false);
        }
    };

    const send = async (text) => {
        // If a photo is staged, take the photo path instead.
        if (pendingPhoto?.file) {
            if (text) setInput(text);
            return sendPhotoMessage();
        }
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setSending(true);
        const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: msg };
        setMessages((m) => [...m, optimistic]);
        setInput("");
        try {
            const res = await sendChat({
                session_id: "default",
                message: msg,
                include_weather: true,
                garden_id: activeGardenId || undefined,
            });
            setMessages((m) => [
                ...m.filter(x => x.id !== optimistic.id),
                res.user_message,
                res.assistant_message,
            ]);
        } catch (e) {
            toast.error(e.response?.data?.detail || "rooteed is napping. Try again later.");
            setMessages((m) => m.filter(x => x.id !== optimistic.id));
        } finally {
            setSending(false);
        }
    };

    const clearAll = async () => {
        if (!window.confirm("Clear all chat history?")) return;
        await clearChat("default");
        setMessages([]);
        toast.success("Chat cleared");
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <div className="space-y-6" data-testid="chat-page">
            <PageHeader
                overline="Your AI gardener"
                title="Talk to rooteed"
                description="Ask anything — weekly plans, succession ideas, applying book notes. The mentor knows your garden."
                action={
                    messages.length > 0 && (
                        <Button variant="ghost" onClick={clearAll} data-testid="clear-chat-btn">
                            <Trash2 className="w-4 h-4 mr-2" /> Clear
                        </Button>
                    )
                }
            />

            <div className="journal-card p-0 overflow-hidden">
                {/* Mentor header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-[var(--vm-border)] bg-[var(--vm-input)]/40">
                    <div className="w-12 h-12 rounded-full overflow-hidden border border-[var(--vm-border)] bg-[var(--vm-input)]">
                        <img src={OWL_AVATAR} alt="rooteed" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <div className="font-serif-display text-xl text-[var(--vm-text)]">rooteed</div>
                        <div className="text-xs text-[var(--vm-text-muted)] flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Powered by Claude Sonnet 4.5
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="h-[55vh] overflow-y-auto px-6 py-6 space-y-4" data-testid="chat-scroll">
                    {loading ? (
                        <div className="text-[var(--vm-text-muted)] flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading conversation…
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-10" data-testid="chat-empty">
                            <p className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">A new conversation</p>
                            <p className="text-sm text-[var(--vm-text-secondary)] mb-6">Try one of these to get started:</p>
                            <div className="flex flex-wrap gap-2 justify-center">
                                {SUGGESTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        data-testid={`suggestion-${SUGGESTIONS.indexOf(s)}`}
                                        className="px-4 py-2 rounded-full border border-[var(--vm-border)] text-sm text-[var(--vm-text-secondary)] hover:bg-[var(--vm-input)] hover:border-[var(--vm-sage)] transition-colors"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        messages.map((m) => (
                            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}-${m.id}`}>
                                {m.role === "assistant" && (
                                    <div className="w-8 h-8 rounded-full overflow-hidden border border-[var(--vm-border)] shrink-0 mt-1">
                                        <img src={OWL_AVATAR} alt="rooteed" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className={`${m.role === "user" ? "max-w-[80%]" : "max-w-[85%]"} flex flex-col`}>
                                    {(m._previewUrl || m.image_data_uri) && m.role === "user" && (
                                        <img
                                            data-testid="chat-msg-image"
                                            src={m._previewUrl || m.image_data_uri}
                                            alt="attached"
                                            className="rounded-lg border border-[var(--vm-border)] max-w-[260px] mb-2 self-end"
                                        />
                                    )}
                                    <div className={`${m.role === "user" ? "bubble-user" : "bubble-ai"} text-sm leading-relaxed whitespace-pre-wrap`}>
                                        {m.content}
                                    </div>
                                    {m.role === "assistant" && m.plan && (
                                        <ChatPlanCard
                                            plan={m.plan}
                                            onApplied={() => refreshGardens?.()}
                                        />
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                    {sending && (
                        <div className="flex gap-3" data-testid="chat-typing">
                            <div className="w-8 h-8 rounded-full overflow-hidden border border-[var(--vm-border)] shrink-0 mt-1">
                                <img src={OWL_AVATAR} alt="rooteed" className="w-full h-full object-cover" />
                            </div>
                            <div className="bubble-ai text-sm text-[var(--vm-text-muted)] flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…
                            </div>
                        </div>
                    )}
                </div>

                {/* Input */}
                <div
                    className={`border-t border-[var(--vm-border)] p-4 bg-[var(--vm-card)] transition-colors ${dragOver ? "bg-[var(--vm-sage)]/10 ring-2 ring-inset ring-[var(--vm-sage)]" : ""}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    data-testid="chat-input-area"
                >
                    {pendingPhoto && (
                        <div className="mb-3 flex items-center gap-3 p-2 rounded-lg bg-[var(--vm-input)] border border-[var(--vm-border)]" data-testid="pending-photo-preview">
                            <img src={pendingPhoto.previewUrl} alt="staged" className="w-14 h-14 rounded object-cover" />
                            <div className="flex-1 text-xs text-[var(--vm-text-muted)]">
                                <div className="font-medium text-[var(--vm-text)] flex items-center gap-1 mb-1">
                                    <ImageIcon className="w-3.5 h-3.5" /> Photo attached
                                </div>
                                <div className="truncate">{pendingPhoto.file.name}</div>
                            </div>
                            <button
                                onClick={() => {
                                    URL.revokeObjectURL(pendingPhoto.previewUrl);
                                    setPendingPhoto(null);
                                }}
                                className="text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] p-1"
                                data-testid="pending-photo-remove"
                                aria-label="Remove photo"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                        onChange={onPickFile}
                        className="hidden"
                        data-testid="chat-photo-input"
                    />
                    <div className="flex gap-3 items-end">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending}
                            className="h-12 w-12 p-0 shrink-0 text-[var(--vm-text-muted)] hover:text-[var(--vm-text)]"
                            data-testid="chat-attach-btn"
                            aria-label="Attach photo"
                        >
                            <Paperclip className="w-5 h-5" />
                        </Button>
                        <Textarea
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={onKey}
                            onPaste={onPaste}
                            placeholder={pendingPhoto ? "Ask about this photo… (optional)" : "Ask rooteed about your garden…"}
                            data-testid="chat-input"
                            rows={2}
                            className="resize-none flex-1 bg-[var(--vm-input)] border-[var(--vm-border)]"
                        />
                        <Button
                            onClick={() => send()}
                            disabled={sending || (!input.trim() && !pendingPhoto)}
                            data-testid="chat-send-btn"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] h-12 w-12 p-0"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="text-xs text-[var(--vm-text-muted)] mt-2">
                        Enter to send · Shift+Enter for new line · Drag or paste a photo to diagnose
                    </div>
                </div>
            </div>
        </div>
    );
}
